import React, { useCallback, useState, useEffect, CSSProperties } from "react";
import { useSelector, useDispatch } from "react-redux";

// components
import {
    Paper,
    Divider,
    Icon,
    TableContainer,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    FormControlLabel,
    Checkbox,
} from "@material-ui/core";
import BouncingDots from "../loading/BouncingDots";
import { useDropzone } from "react-dropzone";

import { IAppState } from "../../types/stores";
import { IEnrichmentFieldImport, IEnrichmentImportField } from "../../types/stores/umbrellaData";
import { IImportLayoutField } from "../../types/stores/externalServiceLayoutData";
import {
    validateImportedEnrichmentFields,
    setIsValidatingEnrichment,
    setValidatedImportFields,
} from "../../actions/flowControlActions";
import { notifyError } from "../../actions/notifyActions";

interface IFileArray {
    LineId: number;
    Columns: Array<string>;
}

type Props = {
    importType: string;
    excludeErrors: boolean;
    excludeErrorsChanged: (value: boolean) => void;
    importedLayoutFields: Array<IImportLayoutField>;
    updateFields: (value: Array<IImportLayoutField>) => void;
    layoutFixed: boolean;
    setLayoutFixed: (value: boolean) => void;
};

const FlowControlFieldImport: React.FC<Props> = ({
    importType,
    excludeErrors,
    excludeErrorsChanged,
    importedLayoutFields,
    updateFields,
    layoutFixed,
    setLayoutFixed,
}: Props) => {
    const dispatch = useDispatch();

    const [importFile, setImportFile] = useState<Array<IFileArray>>([]);
    const [isValidatingLayout, setIsValidatingLayout] = useState(false);
    const [hasErrors, setHasErrors] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const importedUEFields = useSelector<IAppState, Array<IEnrichmentFieldImport>>(
        state => state.umbrellaData.validatedFields
    );

    const isValidatingUE = useSelector((state: IAppState) => state.umbrellaData.isValidating);
    const serviceFields = useSelector((state: IAppState) => state.externalServiceLayoutData.externalServiceFields);
    const flowItemId = useSelector((state: IAppState) => state.selected.flowItem);

    const setIsValidating = useCallback(
        value => {
            if (importType == "ue") {
                dispatch(setIsValidatingEnrichment(value));
            } else {
                setIsValidatingLayout(value);
            }
        },
        [dispatch, importType]
    );

    const returnError = useCallback(
        message => {
            setErrorMessage(message);
            setHasErrors(true);
            setIsValidating(false);
            return false;
        },
        [setIsValidating]
    );

    const processUEFields = useCallback(() => {
        let personHeader = "";
        let fieldHeader = "";
        const personCols: Array<string> = ["person", "personid", "person id"];
        const fieldCols: Array<string> = ["field", "fieldid", "field id"];

        // Has required columns
        const firstRow = importFile.find(x => x.LineId == 1);
        let hasPerson = false;
        let hasField = false;
        if (firstRow && firstRow.Columns) {
            firstRow.Columns.forEach(x => {
                if (personCols.includes(x.toLocaleLowerCase())) {
                    hasPerson = true;
                    personHeader = x;
                }
                if (fieldCols.includes(x.toLocaleLowerCase())) {
                    hasField = true;
                    fieldHeader = x;
                }
            });
        } else {
            return returnError("Error validating headers.");
        }

        // Missing required column(s)
        if (!hasPerson || !hasField) {
            return returnError("Selected file must contain required columns Person and FieldId");
        }

        setHasErrors(false);
        const headers: IFileArray = importFile.filter(x => x.LineId == 1)[0];
        const personCol = headers.Columns.findIndex(x => x == personHeader);
        const fieldCol = headers.Columns.findIndex(x => x == fieldHeader);
        let fieldsToValidate: Array<IEnrichmentImportField> = [];

        importFile.forEach(x => {
            if (x.LineId > 1) {
                const fieldId = x.Columns[fieldCol];
                const personId = x.Columns[personCol];

                const field: IEnrichmentImportField = {
                    Person: parseInt(personId),
                    FieldId: parseInt(fieldId),
                };
                fieldsToValidate.push(field);
            }
        });
        dispatch(validateImportedEnrichmentFields(flowItemId, fieldsToValidate));
    }, [importFile, flowItemId, dispatch, returnError]);

    const processLayout = useCallback(() => {
        const nameCol = "field name";
        const numCol = "column number";
        const posCol = "position";
        const lenCol = "length";
        const encCol = "encryption";
        const requiredCols: Array<string> = [nameCol, numCol, lenCol];
        let headerRowIndex = 0;

        // Find the Header
        importFile.forEach(row => {
            row.Columns.forEach(col => {
                if (requiredCols.includes(col.toLocaleLowerCase())) {
                    headerRowIndex = row.LineId;
                }
            });
        });

        if (headerRowIndex == 0) {
            return returnError("Valid header row not found.");
        }

        const headerRow: IFileArray = importFile.filter(x => x.LineId == headerRowIndex)[0];
        let nameIndex = -1;
        let numberIndex = -1;
        let lenIndex = -1;
        let encIndex = -1;

        if (headerRow && headerRow.Columns) {
            nameIndex = headerRow.Columns.findIndex(x => x.toLocaleLowerCase().trim() == nameCol);
            numberIndex = headerRow.Columns.findIndex(
                x => x.toLocaleLowerCase().trim() == numCol || x.toLocaleLowerCase().trim() == posCol
            );
            lenIndex = headerRow.Columns.findIndex(x => x.toLocaleLowerCase().trim() == lenCol);
            encIndex = headerRow.Columns.findIndex(x => x.toLocaleLowerCase().trim() == encCol);
        } else {
            return returnError("Error validating headers.");
        }

        // Missing required column(s)
        if (nameIndex < 0 || numberIndex < 0 || lenIndex < 0) {
            return returnError("Selected file must contain required columns Field Name, Column Number and Length");
        }

        // Validate
        setHasErrors(false);
        let importedFields: Array<IImportLayoutField> = [];
        let newId = -1;
        let positions: Array<number> = [];
        importFile.forEach(x => {
            if (x.LineId > headerRowIndex) {
                // Validate
                let errorMsgs: Array<string> = [];

                // Field Name
                let standardId = 0;
                let fieldName = x.Columns[nameIndex].trim();
                if (fieldName.length > 0) {
                    // check for standard field
                    const standardField = serviceFields.find(
                        x => x.DisplayName.toLocaleLowerCase().trim() == fieldName.toLocaleLowerCase()
                    );
                    if (standardField) {
                        standardId = standardField.ServiceFieldId;
                        fieldName = standardField.DisplayName;
                    }
                } else {
                    errorMsgs.push("Field Name must be populated.");
                }

                // Postion/Length
                let validPos = true;
                let validLen = true;
                const lenValue = x.Columns[lenIndex].trim();
                const posValue = x.Columns[numberIndex].trim();
                const posLenTxt = " must be a valid number > 0.";
                let error = "";

                if (Number.isNaN(parseInt(posValue)) || parseInt(posValue) == 0) {
                    validPos = false;
                    error = "Position";
                } else if (positions.filter(x => x == parseInt(posValue)).length > 0) {
                    validPos = false;
                    errorMsgs.push("Duplicate Position.");
                } else {
                    positions.push(parseInt(posValue));
                }

                if (Number.isNaN(parseInt(lenValue)) || parseInt(lenValue) == 0) {
                    validLen = false;
                    error = (error.length > 0 ? error + ", " : "") + "Length";
                }
                if (error.length > 0) {
                    errorMsgs.push(error + posLenTxt);
                }

                // Create Field Object
                const field: IImportLayoutField = {
                    Field: {
                        id: newId,
                        LayoutFieldId: newId--,
                        LayoutId: 0,
                        ServiceFieldId: standardId,
                        FieldName: standardId > 0 ? null : fieldName,
                        ShortName: standardId > 0 ? null : fieldName,
                        FieldPosition: validPos ? parseInt(posValue) : 0,
                        FieldLength: validLen ? parseInt(lenValue) : 0,
                        EncryptionType: encIndex >= 0 ? x.Columns[encIndex] : "NONE",
                        IsHardcode: false,
                        FieldAlias: null,
                        AlternateName: null,
                    },
                    DisplayName: fieldName,
                    StringPosition: posValue,
                    StringLength: lenValue,
                    IsValid: errorMsgs.length == 0,
                    Error: errorMsgs.length > 0 ? errorMsgs.join("   ") : "",
                };
                importedFields.push(field);
            }
        });

        updateFields(importedFields);
        setIsValidatingLayout(false);
    }, [importFile, serviceFields, returnError, updateFields]);

    useEffect(
        () => {
            if (importFile.length > 0) {
                importType == "ue" ? processUEFields() : processLayout();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [importFile]
    );

    useEffect(() => {
        if (
            importedUEFields.filter(x => !x.IsValid).length > 0 ||
            importedLayoutFields.filter(x => !x.IsValid).length > 0
        ) {
            returnError("Selected file contains errors");
        }
    }, [importedUEFields, importedLayoutFields, returnError]);

    const readLines = useCallback(
        (file, forEachLine, onComplete) => {
            setIsValidating(true);
            const CHUNK_SIZE = 100000; // 100kb, arbitrarily chosen.
            const decoder = new TextDecoder();
            let offset = 0;
            let results: string | undefined = "";
            const fr = new FileReader();
            fr.onload = () => {
                // Use stream:true in case we cut the file
                // in the middle of a multi-byte character
                results += decoder.decode(fr.result as ArrayBuffer, { stream: true });
                const lines = (results && results.split("\n")) || [];
                results = lines.pop(); // In case the line did not end yet.

                for (let i = 0; i < lines.length; ++i) {
                    forEachLine(lines[i] + "\n");
                }
                offset += CHUNK_SIZE;
                seek();
            };
            fr.onerror = () => {
                dispatch(notifyError("Error reading file: " + fr.error));
            };

            const seek = () => {
                if (offset !== 0 && offset >= file.size) {
                    // We did not find all lines, but there are no more lines.
                    forEachLine(results); // This is from lines.pop(), before.
                    onComplete(); // Done
                    return;
                }
                const slice = file.slice(offset, offset + CHUNK_SIZE);
                fr.readAsArrayBuffer(slice);
            };
            seek();
        },
        [dispatch, setIsValidating]
    );

    const readFile = useCallback(
        file => {
            let lineno = 1;
            let fileArray: Array<IFileArray> = [];

            const onComplete = () => {
                setImportFile(fileArray);
            };

            readLines(
                file,
                line => {
                    if (line.length > 0) {
                        let insideQuote = false;
                        const columns: Array<string> = [];
                        let entry: Array<string> = [];
                        line.split("").forEach(character => {
                            if (character === '"') {
                                insideQuote = !insideQuote;
                            } else if (character == "," && !insideQuote) {
                                columns.push(entry.join(""));
                                entry = [];
                            } else {
                                entry.push(character);
                            }
                        });

                        columns.push(entry.join("").trim());

                        fileArray.push({
                            LineId: lineno,
                            Columns: columns,
                        });
                    }
                    lineno++;
                },
                onComplete
            );
        },
        [readLines]
    );

    const onDrop = useCallback(
        acceptedFiles => {
            dispatch(setValidatedImportFields([]));
            updateFields([]);
            readFile([...acceptedFiles][0]);
        },
        [readFile, updateFields, dispatch]
    );

    const tableHead: CSSProperties = {
        fontWeight: "bold",
        fontSize: 16,
        alignSelf: "left",
    };

    const tableCell: CSSProperties = {
        fontSize: 14,
        alignSelf: "left",
        padding: "5px 16px 5px 16px",
    };

    const textCell: CSSProperties = {
        fontSize: 14,
        alignSelf: "left",
        padding: "5px 16px 5px 16px",
        whiteSpace: "nowrap",
        maxWidth: "400px",
        overflow: "hidden",
        textOverflow: "ellipsis",
    };

    const renderUETable = () => (
        <div>
            <Paper>
                <div>
                    <TableContainer style={{ maxHeight: "33vh" }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center" style={tableHead}>
                                        Person
                                    </TableCell>
                                    <TableCell align="left" style={tableHead}>
                                        Field Id
                                    </TableCell>
                                    <TableCell align="left" style={tableHead}>
                                        Field Name
                                    </TableCell>
                                    <TableCell align="left" style={tableHead}>
                                        Description
                                    </TableCell>
                                    {hasErrors && (
                                        <TableCell align="left" style={tableHead}>
                                            Error
                                        </TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            {importedUEFields.length == 0 ? (
                                <TableBody>
                                    <TableRow>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell
                                            style={{
                                                fontSize: 18,
                                                alignSelf: "center",
                                                padding: "15px 16px 15px 16px",
                                                fontWeight: 400,
                                            }}
                                        >
                                            No Fields Found
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            ) : (
                                <TableBody>
                                    {importedUEFields.map((field, i) => (
                                        <TableRow
                                            key={i}
                                            style={!field.IsValid ? { backgroundColor: "#fcf8e3" } : undefined}
                                        >
                                            <TableCell align="center" style={tableCell}>
                                                {field.Person}
                                            </TableCell>
                                            <TableCell align="left" style={tableCell}>
                                                {field.EnrichmentField.FieldId}
                                            </TableCell>
                                            <TableCell align="left" style={textCell}>
                                                {field.EnrichmentField.FieldName}
                                            </TableCell>
                                            <TableCell align="left" style={textCell}>
                                                {field.EnrichmentField.ShortDescription}
                                            </TableCell>
                                            {hasErrors && (
                                                <TableCell align="left" style={tableCell}>
                                                    {field.Error}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                </div>
            </Paper>
        </div>
    );

    const renderLayoutTable = () => (
        <div>
            <Paper>
                <div>
                    <TableContainer style={{ maxHeight: "33vh" }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left" style={tableHead}>
                                        Field Name
                                    </TableCell>
                                    <TableCell align="center" style={tableHead}>
                                        Column / Position
                                    </TableCell>
                                    <TableCell align="center" style={tableHead}>
                                        Length
                                    </TableCell>
                                    <TableCell align="left" style={tableHead}>
                                        Encryption
                                    </TableCell>
                                    {hasErrors && (
                                        <TableCell align="left" style={tableHead}>
                                            Error
                                        </TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            {importedLayoutFields.length == 0 ? (
                                <TableBody>
                                    <TableRow>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell
                                            style={{
                                                fontSize: 18,
                                                alignSelf: "center",
                                                padding: "15px 16px 15px 16px",
                                                fontWeight: 400,
                                            }}
                                        >
                                            No Fields Found
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            ) : (
                                <TableBody>
                                    {importedLayoutFields.map((field, i) => (
                                        <TableRow
                                            key={i}
                                            style={!field.IsValid ? { backgroundColor: "#fcf8e3" } : undefined}
                                        >
                                            <TableCell align="left" style={textCell}>
                                                {field.DisplayName}
                                            </TableCell>
                                            <TableCell align="center" style={textCell}>
                                                {field.StringPosition}
                                            </TableCell>
                                            <TableCell align="center" style={textCell}>
                                                {field.StringLength}
                                            </TableCell>
                                            <TableCell align="left" style={textCell}>
                                                {field.Field.EncryptionType}
                                            </TableCell>
                                            {hasErrors && (
                                                <TableCell align="left" style={tableCell}>
                                                    {field.Error}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                </div>
            </Paper>
        </div>
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: ".csv" });

    const renderFixedCheckbox = () => (
        <FormControlLabel
            labelPlacement="start"
            title="IsFixed"
            control={
                <Checkbox
                    id="IsFixedCheck"
                    color="primary"
                    checked={layoutFixed}
                    onChange={e => setLayoutFixed(e.target.checked)}
                    value={layoutFixed}
                />
            }
            label="Fixed Width"
        />
    );

    return (
        <div>
            <div id="browse-files-modal" style={{ overflow: "hidden", display: "flex", justifyContent: "center" }}>
                <div className="my-computer-files-container" style={{ width: "100%" }}>
                    <div {...getRootProps()} className="browse-drag-and-drop-container">
                        <input {...getInputProps()} className="browse-drag-and-drop-container-label" />
                        {isDragActive ? (
                            <p>Drop the files here ...</p>
                        ) : (
                            <>
                                <i className="material-icons">folder</i>
                                &nbsp;&nbsp; Drag & drop your files here or click here to browse on your computer ...
                            </>
                        )}
                    </div>
                    <Divider style={{ width: "100%" }} />
                    <div className="browse-integration-desc-container">
                        <span>Other information</span>
                        <ol>
                            <li>Currently only accepts .csv files.</li>
                        </ol>
                    </div>
                    <Divider style={{ width: "100%", marginBottom: "20px" }} />
                </div>
            </div>
            {importType == "layout" ? renderFixedCheckbox() : null}
            {isValidatingUE || isValidatingLayout ? (
                <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
                    <BouncingDots />
                </div>
            ) : (
                <div>
                    <div>
                        {hasErrors && (
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <div
                                    className="alert alert-warning-original flow-item-warnings"
                                    style={{ marginTop: 5, width: "75%", padding: "5px", marginBottom: 0 }}
                                >
                                    <div>
                                        <Icon style={{ marginRight: "10px", position: "relative", top: "2px" }}>
                                            error_outline
                                        </Icon>
                                        {errorMessage}
                                    </div>
                                </div>
                                {importedUEFields.filter(x => x.IsValid).length > 0 && (
                                    <FormControlLabel
                                        style={{ marginLeft: "20px" }}
                                        labelPlacement="end"
                                        title="Exclude errors and import"
                                        control={
                                            <Checkbox
                                                id="exclude-errors"
                                                color="primary"
                                                onChange={e => excludeErrorsChanged(e.target.checked)}
                                                checked={excludeErrors}
                                                value={excludeErrors}
                                                disabled={!hasErrors}
                                            />
                                        }
                                        label="Exclude Errors"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    {importedUEFields.length > 0 && renderUETable()}
                    {importedLayoutFields.length > 0 && renderLayoutTable()}
                </div>
            )}
        </div>
    );
};

export default FlowControlFieldImport;
